/** Workspace log sources grouped for Log Explorer. */

export function sourceHasApplication(source, applicationName) {
  if (!applicationName || !source) {
    return true;
  }
  const needle = applicationName.trim().toLowerCase();
  const names = [
    source.applicationName,
    ...(Array.isArray(source.applications) ? source.applications : [])
  ];
  return names.some((app) => String(app || "").trim().toLowerCase() === needle);
}

export function sourceIdentityKeys(source, includeIndex) {
  return [
    includeIndex ? source?.indexName : "",
    source?.applicationName,
    source?.serviceName,
    ...(Array.isArray(source?.applications) ? source.applications : [])
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
}

export function applicationNames(source) {
  const names = [
    source?.applicationName,
    ...(Array.isArray(source?.applications) ? source.applications : [])
  ];
  return [...new Set(names.map((name) => String(name || "").trim()).filter(Boolean))].sort();
}

/** Group configured sources: index → application → environment / platform + source rows. */
export function buildLogSourceTree(sources) {
  const indexes = new Map();
  (sources || []).forEach((source) => {
    const indexName = String(source?.indexName || "").trim();
    if (!indexName) return;
    if (!indexes.has(indexName)) indexes.set(indexName, new Map());
    const apps = indexes.get(indexName);
    const names = applicationNames(source);
    const targets = names.length > 0 ? names : [""];
    targets.forEach((name) => {
      if (!apps.has(name)) {
        apps.set(name, { environments: new Set(), platforms: new Set(), sources: [] });
      }
      const node = apps.get(name);
      const environment = String(source.environment || "").trim();
      const platform = String(source.platform || "").trim();
      if (environment) node.environments.add(environment);
      if (platform) node.platforms.add(platform);
      if (!node.sources.some((row) => row.id === source.id && source.id != null)) {
        node.sources.push(source);
      } else if (source.id == null) {
        node.sources.push(source);
      }
    });
  });
  return [...indexes.keys()].sort().map((indexName) => {
    const apps = indexes.get(indexName);
    return {
      indexName,
      applications: [...apps.keys()].sort().filter(Boolean).map((name) => {
        const node = apps.get(name);
        return {
          name,
          environments: [...node.environments].sort(),
          platforms: [...node.platforms].sort(),
          sources: node.sources
        };
      })
    };
  });
}

export function resolveSourcesForSelection(sources, selectedIndex, selectedApplication) {
  if (!Array.isArray(sources)) return [];
  return sources.filter((source) => {
    if (selectedIndex && source.indexName !== selectedIndex) return false;
    if (selectedApplication && !sourceHasApplication(source, selectedApplication)) return false;
    return true;
  });
}

export function filterLogsForSelectedIndex(logs, selectedIndex, selectedApplication, sources) {
  if (!Array.isArray(logs) || !selectedIndex) return [];
  const matched = resolveSourcesForSelection(sources, selectedIndex, selectedApplication);
  const keys = new Set(matched.flatMap((source) => sourceIdentityKeys(source, !selectedApplication)));
  if (keys.size === 0) return [];
  return logs.filter((log) => {
    const service = String(log.service || "").trim().toLowerCase();
    const application = String(log.application || log.app || "").trim().toLowerCase();
    return keys.has(service) || (application && keys.has(application));
  });
}
